// src/pages/PublicOrderPortal.jsx
// Kundens side for en kundeordre (/kundeordre/<token>, vinnsel 29.10) eller et tekstiltrykk
// (/kundetrykk/<token>, sti="services", vinnsel 30.10), fra SMS-en butikken sender. Viser saken og
// status, pristilbudet hvis det er sendt, og meldinger med butikken. Backend: src/lib/kundelenke.js
// (svarer bare med det kunden trenger). Saken hører til butikken: Sport 1-logo og butikkens
// kontaktinfo, som pristilbudet (PublicOfferPortal.jsx).
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useSideTittel } from "../components/tilgjengelighet";
import { butikkFor } from "../tilbud/butikker";
import { SPORT1_LOGO } from "../kundesider";

const API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/+$/, "");

const fmtDT = (d) => {
  const dt = d ? new Date(d) : null;
  return dt && !isNaN(+dt)
    ? dt.toLocaleString("no-NO", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false }).replace(",", "")
    : "";
};

const STATUSFARGE = {
  Bestilt: "bg-blue-100 text-blue-900",
  "Klar for henting": "bg-green-100 text-green-900",
  Avsluttet: "bg-gray-100 text-gray-800",
};

// Ordre lagrer «d.M.yyyy, HH:mm:ss», trykk ISO-dato.
const datoTekst = (v) => {
  if (!v) return "";
  const s = String(v);
  if (/^\d{1,2}\.\d{1,2}\.\d{4}/.test(s)) return s.split(",")[0];
  const d = new Date(s);
  return isNaN(+d) ? "" : d.toLocaleDateString("no-NO", { day: "2-digit", month: "2-digit", year: "numeric" });
};

export default function PublicOrderPortal({ sti = "orders" }) {
  const { token } = useParams();
  const [ordre, setOrdre] = useState(null);
  const [feil, setFeil] = useState("");
  const [tekst, setTekst] = useState("");
  const [sender, setSender] = useState(false);
  const [sendFeil, setSendFeil] = useState("");
  const [status, setStatus] = useState("");
  const listeRef = useRef(null);

  const trykk = sti === "services";
  const tittel = ordre?.tittel || (ordre ? (ordre.type === "skiplukk" ? "Skiplukk" : `Ordre ${ordre.ordreid ?? ""}`) : trykk ? "Trykk" : "Ordre");
  useSideTittel(`${tittel} – ${ordre?.butikk || "Sport 1"}`);

  useEffect(() => {
    let avbrutt = false;
    const hent = () =>
      fetch(`${API_BASE_URL}/${sti}/public/${encodeURIComponent(token)}`)
        .then(async (r) => {
          const d = await r.json().catch(() => ({}));
          if (!r.ok) throw new Error(d.message || "Fant ikke ordren");
          return d;
        })
        .then((d) => { if (!avbrutt) { setOrdre(d); setFeil(""); } })
        .catch((e) => { if (!avbrutt) setFeil(e.message); });
    hent();
    // Nye svar fra butikken når kunden kommer tilbake til fanen.
    const vedFokus = () => { if (document.visibilityState === "visible") hent(); };
    document.addEventListener("visibilitychange", vedFokus);
    return () => { avbrutt = true; document.removeEventListener("visibilitychange", vedFokus); };
  }, [token, sti]);

  useEffect(() => {
    if (listeRef.current) listeRef.current.scrollTop = listeRef.current.scrollHeight;
  }, [ordre?.meldinger?.length]);

  const send = async (e) => {
    e.preventDefault();
    if (!tekst.trim()) return;
    setSender(true);
    setSendFeil("");
    setStatus("");
    try {
      const r = await fetch(`${API_BASE_URL}/${sti}/public/${encodeURIComponent(token)}/meldinger`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ tekst }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.message || "Kunne ikke sende meldingen");
      setOrdre(d);
      setTekst("");
      setStatus("Meldingen er sendt. Butikken svarer så snart de kan.");
    } catch (err) {
      setSendFeil(err.message);
    } finally {
      setSender(false);
    }
  };

  if (feil) {
    return (
      <div className="mx-auto w-full max-w-2xl px-3 sm:px-4 py-10">
        <img src={SPORT1_LOGO} alt="Sport 1" className="mb-6 h-10 w-auto object-contain" />
        <h1 className="text-xl font-bold text-gray-900 mb-2">{trykk ? "Trykk" : "Ordre"}</h1>
        <p className="text-gray-800" role="alert">{feil}</p>
      </div>
    );
  }
  if (!ordre) {
    return <p className="mx-auto max-w-2xl px-4 py-10 text-gray-700" role="status">{trykk ? "Henter trykket …" : "Henter ordren …"}</p>;
  }

  const butikk = butikkFor(ordre.butikkid);
  const vare = ordre.vare;
  const meldinger = ordre.meldinger || [];

  return (
    <div className="mx-auto w-full max-w-2xl px-3 sm:px-4 py-5 sm:py-10 space-y-5">
      <img src={SPORT1_LOGO} alt={butikk.navn} className="h-10 w-auto object-contain" />

      <section className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-gray-900">{tittel}</h1>
            <p className="text-sm text-gray-700">{butikk.navn}{datoTekst(ordre.registrert) ? ` · registrert ${datoTekst(ordre.registrert)}` : ""}</p>
          </div>
          <span className={`shrink-0 rounded-full px-3 py-1 text-sm font-semibold ${STATUSFARGE[ordre.status] || STATUSFARGE.Bestilt}`}>
            {ordre.status}
          </span>
        </div>

        {vare ? (
          <dl className="mt-4 grid grid-cols-1 gap-x-6 gap-y-2 text-sm sm:grid-cols-2">
            {[["Merke", vare.merke], ["Produkt", vare.produkt], ["Størrelse", vare.storrelse], ["Farge", vare.farge]]
              .filter(([, v]) => v)
              .map(([k, v]) => (
                <div key={k} className="min-w-0">
                  <dt className="text-gray-600">{k}</dt>
                  <dd className="font-medium text-gray-900 break-words">{v}</dd>
                </div>
              ))}
          </dl>
        ) : (
          <p className="mt-3 text-sm text-gray-800">Vi hjelper deg å finne riktige ski. Her får du beskjed og kan sende oss spørsmål.</p>
        )}
        {ordre.ferdig && !ordre.stengt && (
          <p className="mt-3 text-sm text-gray-800">Lovet ferdig: <span className="font-medium">{datoTekst(ordre.ferdig)}</span></p>
        )}
        {ordre.status === "Klar for henting" && (
          <p className="mt-3 rounded-lg bg-green-50 px-3 py-2 text-sm text-green-900">
            {vare ? "Varen" : "Skiene"} er klar for henting hos {butikk.navn}. Du betaler i kassa når du henter.
          </p>
        )}
      </section>

      {ordre.tilbud && (
        <section aria-labelledby="ko-tilbud" className="rounded-xl border border-amber-200 bg-amber-50 p-4 sm:p-5">
          <h2 id="ko-tilbud" className="text-lg font-semibold text-gray-900">Pristilbud</h2>
          <p className="mt-1 text-sm text-gray-800">
            {ordre.tilbud.status === "godkjent" ? "Du har godkjent pristilbudet." : ordre.tilbud.status === "avslatt" ? "Du har avslått pristilbudet." : "Du har fått et pristilbud. Se det og svar på lenken."}
          </p>
          <a href={ordre.tilbud.lenke} className="mt-2 inline-block font-semibold text-blue-800 underline">Åpne pristilbudet</a>
        </section>
      )}

      <section aria-labelledby="ko-meldinger" className="rounded-xl border border-gray-200 bg-white p-4 sm:p-5 shadow-sm space-y-3">
        <h2 id="ko-meldinger" className="text-lg font-semibold text-gray-900">Meldinger med butikken</h2>
        {meldinger.length ? (
          <ul ref={listeRef} className="max-h-96 space-y-2 overflow-y-auto" aria-label="Meldinger">
            {meldinger.map((m) => {
              const fraButikk = m.fra === "butikk";
              return (
                <li key={m._id || m.dato} className={`flex ${fraButikk ? "justify-start" : "justify-end"}`}>
                  <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm ${fraButikk ? "bg-gray-100 text-gray-900" : "bg-blue-700 text-white"}`}>
                    <p className="text-xs font-medium">
                      {fraButikk ? m.navn || butikk.navn : "Deg"}
                      <span className="sr-only">, </span>
                      <span className="ml-2">{fmtDT(m.dato)}</span>
                    </p>
                    <p className="mt-1 whitespace-pre-wrap break-words">{m.tekst}</p>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-sm text-gray-700">Ingen meldinger ennå. Har du spørsmål om {trykk ? "trykket" : vare ? "ordren" : "skiene"}, kan du skrive til oss her.</p>
        )}

        {ordre.stengt ? (
          <p className="text-sm text-gray-700">{trykk ? "Trykket" : "Ordren"} er avsluttet. Har du spørsmål, ta kontakt med {butikk.navn}.</p>
        ) : (
          <form onSubmit={send} className="space-y-2">
            <label htmlFor="ko-melding" className="block text-sm font-medium text-gray-800">Melding til {butikk.navn}</label>
            <textarea
              id="ko-melding"
              rows={3}
              maxLength={2000}
              value={tekst}
              onChange={(e) => setTekst(e.target.value)}
              aria-invalid={sendFeil ? true : undefined}
              aria-describedby={sendFeil ? "ko-melding-feil" : undefined}
              className="w-full rounded-lg border border-gray-400 px-3 py-2 text-base"
            />
            {sendFeil && <p id="ko-melding-feil" className="text-sm text-red-700" role="alert">{sendFeil}</p>}
            <button
              type="submit"
              disabled={sender || !tekst.trim()}
              className="rounded-lg bg-blue-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-800 disabled:opacity-50"
            >
              {sender ? "Sender …" : "Send melding"}
            </button>
          </form>
        )}
        <p className="text-sm text-gray-800" role="status">{status}</p>
      </section>

      <p className="text-center text-xs text-gray-600 pb-6 [overflow-wrap:anywhere]">
        {butikk.selskap} · {butikk.adresse} · Tlf. {butikk.telefon} ·{" "}
        <a href={`mailto:${butikk.epost}`} className="underline">{butikk.epost}</a>
      </p>
    </div>
  );
}
